/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import * as userDataAction from 'app/shared/store/actions/user-data.action';
import * as accessAction from 'app/shared/store/actions/access.action';
import * as fromServices from 'app/shared/services/';
import { Constants } from '@shared/models/constants.model';
import { LoadUserAccess, ResetUserAccess } from '../actions';
import { SharedState } from '../reducers';

@Injectable()
export class UserDataEffects {
  getUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userDataAction.GET_USER_BY_TOKEN),
      // don't request to the server that often
      debounceTime(200),
      switchMap((action: userDataAction.GetUserByToken) => {
        return this.userDataService.getUserByToken(action.payload).pipe(
          mergeMap(this.getHeadCoachClips.bind(this), this.setHeadCoachClips.bind(this)),
          tap((data) => {
            this.wsService.registerAsUser(data.id);
            this.gaService.isLoggedIn = true;
            this.store.dispatch(new LoadUserAccess({ userId: data.id, userType: data.type }));
          }),
          map((response) => {
            return new userDataAction.GetUserByTokenSuccess(this.augmentUserData(response));
          }),
          catchError((error) => {
            this.userDataService.deleteExistingToken().subscribe();
            return of(new userDataAction.GetUserByTokenFail(error.message));
          })
        );
      })
    )
  );

  getUserDataByID$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userDataAction.GET_USER_BY_ID),
      switchMap((action: userDataAction.GetUserById) => {
        return this.userDataService.getUserById(action.payload).pipe(
          mergeMap(this.getHeadCoachClips.bind(this), this.setHeadCoachClips.bind(this)),
          map((response) => new userDataAction.GetUserByIdSuccess(this.augmentUserData(response))),
          catchError((error) => of(new userDataAction.GetUserByIdFail(error.message)))
        );
      })
    )
  );

  updateUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userDataAction.UPDATE_USER_DATA),
      switchMap((action: userDataAction.UpdateUserData) => {
        return this.userDataService.updateUser(action.payload).pipe(
          mergeMap(this.getHeadCoachClips.bind(this), this.setHeadCoachClips.bind(this)),
          map((response) => new userDataAction.UpdateUserDataSuccess(this.augmentUserData(response))),
          catchError((error) => of(new userDataAction.UpdateUserDataFail(error.message)))
        );
      })
    )
  );

  refreshUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userDataAction.REFRESH_USER_DATA),
      switchMap(() => {
        return this.userDataService.getUserData().pipe(
          mergeMap(this.getHeadCoachClips.bind(this), this.setHeadCoachClips.bind(this)),
          map((response) => new userDataAction.RefreshUserDataSuccess(this.augmentUserData(response))),
          catchError((error) => of(new userDataAction.RefreshUserDataFail(error.message)))
        );
      })
    )
  );

  sendLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userDataAction.USER_LOGIN),
      switchMap((action: userDataAction.UserLogin) => {
        return this.userDataService.login(action.payload).pipe(
          tap((response) => this.userDataService.setToken(response.access_token, action.payload.rememberMe)),
          map((response) => response.user),
          tap((user) =>
            this.utilsService.logToServer(
              Constants.LOG_TYPES.DEVICE_INFO,
              { id: user.id, username: user.username },
              navigator.userAgent
            )
          ),
          tap((user) => {
            this.wsService.registerAsUser(user.id);
            this.gaService.isLoggedIn = true;
            this.store.dispatch(new accessAction.LoadUserAccess({ userId: user.id, userType: user.type }));
          }),
          mergeMap(this.getHeadCoachClips.bind(this), this.setHeadCoachClips.bind(this)),
          map((user) => new userDataAction.UserLoginSuccess(this.augmentUserData(user))),
          catchError((error) => of(new userDataAction.UserLoginFail(error.message)))
        );
      })
    )
  );

  doLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userDataAction.USER_LOGOUT),
      switchMap((action: userDataAction.UserLogout) => {
        return this.userDataService.logout().pipe(
          tap(() => {
            this.gaService.isLoggedIn = false;
            this.wsService.unregister();
            this.store.dispatch(new ResetUserAccess());
          }),
          map((response) => new userDataAction.UserLogoutSuccess(response)),
          catchError((error) => of(new userDataAction.UserLogoutFail(error.message)))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private gaService: fromServices.GoogleAnalyticsService,
    private userDataService: fromServices.UserDataService,
    private utils: fromServices.UtilsService,
    private store: Store<SharedState>,
    private wsService: fromServices.WebsocketsService,
    private utilsService: fromServices.UtilsService
  ) {}

  private augmentUserData(user) {
    user.isAthlete = user.type === Constants.USER_TYPE.ATHLETE;

    if (user.image) {
      user.avatarImage = user.image.urlThumb;
    } else {
      user.avatarImage = Constants.DEFAULT_AVATAR_ICON_SVG;
    }

    // get profile url
    const targetProfileNameSlug = this.utils.getSlugForName(user.firstName + ' ' + (user.lastName || ''));
    user.profileLink = user.profileUrl ? ['/profile', user.profileUrl] : ['/profile', targetProfileNameSlug, user.id];

    return user;
  }

  private getHeadCoachClips(userData) {
    if (userData.type === Constants.USER_TYPE.HEAD_COACH) {
      return this.userDataService.getUserClipsCount(userData.id);
    }
    return of(false);
  }

  private setHeadCoachClips(userData, clipsCount: any) {
    if (clipsCount) {
      const hasClips = clipsCount ? parseInt(clipsCount) > 0 : false;
      return {
        ...userData,
        hasClips,
      };
    }
    return userData;
  }
}
